import React from "react"
import { Helmet } from "react-helmet-async"
import { RefreshCw, Calendar, CheckCircle, XCircle, Phone, AlertTriangle } from "lucide-react"

const Returns: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Returns & Exchanges - Anmol Furniture Bazar</title>
        <meta 
          name="description" 
          content="Easy returns and exchanges at Anmol Furniture Bazar. 30-day return policy, hassle-free process. Learn about our return conditions and process." 
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Returns & Exchanges</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We want you to love your furniture. If you're not completely satisfied, we're here to help.
            </p>
          </div>

          {/* Return Policy Banner */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-xl mb-12 text-center">
            <div className="flex items-center justify-center mb-2">
              <RefreshCw className="w-8 h-8 mr-2" />
              <h2 className="text-2xl font-bold">30-DAY RETURN POLICY</h2>
            </div>
            <p className="text-lg">Easy returns and exchanges on eligible items</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Return Conditions */}
            <div className="space-y-8">
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                  <CheckCircle className="w-6 h-6 mr-2 text-green-600" />
                  What Can Be Returned
                </h2>
                <div className="space-y-4">
                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Standard Furniture Items</h3>
                    <ul className="text-gray-700 space-y-1 text-sm">
                      <li>• Sofas, chairs, and seating</li>
                      <li>• Tables and storage furniture</li>
                      <li>• Beds and bedroom furniture</li>
                      <li>• TV units and stands</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Return Conditions</h3>
                    <ul className="text-gray-700 space-y-1 text-sm">
                      <li>• Item must be in original condition</li>
                      <li>• Original packaging and tags intact</li>
                      <li>• No damage or stains</li>
                      <li>• Returned within 30 days of delivery</li>
                      <li>• Original invoice/receipt required</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                  <XCircle className="w-6 h-6 mr-2 text-red-600" />
                  What Cannot Be Returned
                </h2>
                <div className="space-y-4">
                  <div className="border-l-4 border-red-500 pl-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Non-Returnable Items</h3>
                    <ul className="text-gray-700 space-y-1 text-sm">
                      <li>• Custom-made furniture</li>
                      <li>• Made-to-order items</li>
                      <li>• Personalized furniture</li>
                      <li>• Mattresses and cushions</li>
                      <li>• Items damaged by customer</li>
                      <li>• Items returned after 30 days</li>
                    </ul>
                  </div>
                  
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                      <div>
                        <p className="text-red-800 font-semibold text-sm">Important Note</p>
                        <p className="text-red-700 text-sm">Items with hygiene concerns or those that have been assembled cannot be returned for health and safety reasons.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Return Process */}
            <div className="space-y-8">
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                  <Calendar className="w-6 h-6 mr-2 text-primary-600" />
                  Return Process
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-bold text-sm">1</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Contact Us</h3>
                      <p className="text-gray-600 text-sm">Call us within 30 days of delivery to initiate return</p>
                      <p className="text-primary-600 text-sm font-medium">+91 8010271912</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-bold text-sm">2</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Return Authorization</h3>
                      <p className="text-gray-600 text-sm">We'll provide a Return Authorization Number and instructions</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-bold text-sm">3</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Item Inspection</h3>
                      <p className="text-gray-600 text-sm">Our team will inspect the item condition upon pickup</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-bold text-sm">4</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Refund Processing</h3>
                      <p className="text-gray-600 text-sm">Approved refunds processed within 7-10 business days</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Exchange Policy</h2>
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-blue-900 mb-2">Size/Color Exchange</h3>
                    <p className="text-blue-800 text-sm">Exchange for different size or color within 15 days, subject to availability</p>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h3 className="font-semibold text-green-900 mb-2">Defective Items</h3>
                    <p className="text-green-800 text-sm">Immediate replacement for manufacturing defects or damage during transit</p>
                  </div>
                  
                  <div className="space-y-2 pt-2">
                    <h3 className="font-semibold text-gray-900">Exchange Conditions:</h3>
                    <ul className="text-gray-700 space-y-1 text-sm ml-4">
                      <li>• Same item category only</li>
                      <li>• Price difference to be paid if applicable</li>
                      <li>• Original condition maintained</li>
                      <li>• Subject to stock availability</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Refund Information */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Refund Methods</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Online Payment</span>
                  <span className="font-medium text-primary-600">Original Payment Method</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Cash Payment</span>
                  <span className="font-medium text-primary-600">Bank Transfer</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-700">Processing Time</span>
                  <span className="font-medium text-primary-600">7-10 Business Days</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Return Charges</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Defective/Damaged Items</span>
                  <span className="font-medium text-green-600">FREE</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Customer Change of Mind</span>
                  <span className="font-medium text-orange-600">₹500 - ₹2,000</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-700">Pickup & Restocking</span>
                  <span className="font-medium text-orange-600">Based on Item Size</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="mt-12 text-center bg-white p-8 rounded-xl shadow-lg">
            <Phone className="w-12 h-12 text-primary-600 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Need Help with Returns?</h2>
            <p className="text-gray-600 mb-6">Our customer service team is here to make your return process smooth and hassle-free</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold text-gray-900">Call</p>
                <p className="text-primary-600">+91 8010271912</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold text-gray-900">WhatsApp</p>
                <p className="text-primary-600">+91 9987023567</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold text-gray-900">Email</p>
                <p className="text-primary-600">support@anmolfurniture.com</p>
              </div>
            </div>
          </div>

          {/* Important Notice */}
          <div className="mt-8 bg-yellow-50 p-6 rounded-xl border border-yellow-200">
            <h3 className="text-lg font-semibold text-yellow-900 mb-3">Important Return Guidelines</h3>
            <ul className="space-y-2 text-yellow-800 text-sm">
              <li className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>Return timeline starts from the date of delivery, not the date of purchase</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>All returns are subject to inspection and approval by our quality team</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>Refund amount may be adjusted for any damage or missing components</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>Custom orders and personalized items are final sale and cannot be returned</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  )
}

export default Returns